/**
 * Configuración de la institución (solo Administración).
 *
 * Estos datos se imprimen en los recibos, pero **los recibos ya emitidos no
 * cambian**: se regeneran desde los datos inmutables del pago, no desde la
 * configuración actual. Cambiar el nombre hoy no reescribe un comprobante de hace
 * un mes, y la pantalla lo dice para que nadie lo suponga al revés.
 *
 * La zona horaria es el campo delicado: de ella dependen la fecha operativa, el
 * cálculo de la mora y la numeración diaria de descargas.
 */

import { useEffect, useState } from 'react';
import type { InstitutionDto } from '@senm/contracts';
import { ApiRequestError, apiRequest } from '../api/client.js';
import type { StoredSession } from '../local/session.js';

/**
 * Zonas horarias ofrecidas. Se listan las de la región en vez de dejar un campo
 * libre: un identificador IANA mal escrito rompería el cálculo de la fecha
 * operativa, y con él la mora. El servidor valida igualmente.
 */
const ZONAS = [
  'America/Guatemala',
  'America/El_Salvador',
  'America/Tegucigalpa',
  'America/Managua',
  'America/Costa_Rica',
  'America/Panama',
  'America/Mexico_City',
  'America/Bogota',
  'UTC',
];

export function InstitutionConfigPage(props: {
  session: StoredSession;
  conexion: string;
}): JSX.Element {
  const [institucion, setInstitucion] = useState<InstitutionDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const [nombre, setNombre] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [nit, setNit] = useState('');
  const [direccion, setDireccion] = useState('');
  const [telefono, setTelefono] = useState('');
  const [zona, setZona] = useState('America/Guatemala');
  const [permiteExcedente, setPermiteExcedente] = useState(true);

  const aplicar = (datos: InstitutionDto): void => {
    setInstitucion(datos);
    setNombre(datos.name);
    setRazonSocial(datos.legalName ?? '');
    setNit(datos.taxId ?? '');
    setDireccion(datos.address ?? '');
    setTelefono(datos.phone ?? '');
    setZona(datos.timeZone);
    setPermiteExcedente(datos.allowOverpayment);
  };

  useEffect(() => {
    void (async () => {
      try {
        aplicar(await apiRequest<InstitutionDto>('/v1/institution'));
      } catch (problema) {
        setError(
          problema instanceof ApiRequestError && problema.isNetworkFailure
            ? 'La configuración necesita conexión con el servidor.'
            : 'No fue posible cargar la configuración.',
        );
      }
    })();
  }, []);

  const guardar = async (evento: React.FormEvent): Promise<void> => {
    evento.preventDefault();
    if (institucion === null || guardando) return;
    setGuardando(true);
    setError(null);
    setAviso(null);
    try {
      const actualizada = await apiRequest<InstitutionDto>('/v1/institution', {
        method: 'PATCH',
        body: {
          name: nombre,
          legalName: razonSocial.trim() === '' ? null : razonSocial,
          taxId: nit.trim() === '' ? null : nit,
          address: direccion.trim() === '' ? null : direccion,
          phone: telefono.trim() === '' ? null : telefono,
          timeZone: zona,
          allowOverpayment: permiteExcedente,
          expectedVersion: institucion.version,
        },
      });
      aplicar(actualizada);
      setAviso('Configuración guardada. Los recibos ya emitidos no cambiaron.');
    } catch (problema) {
      setError(
        problema instanceof ApiRequestError
          ? problema.message
          : 'No fue posible guardar la configuración.',
      );
    } finally {
      setGuardando(false);
    }
  };

  if (props.conexion !== 'EN_LINEA') {
    return (
      <section aria-labelledby="titulo-config">
        <h2 id="titulo-config">Configuración de la institución</h2>
        <div className="aviso-caja" role="status">
          Esta sección necesita conexión con el servidor.
        </div>
      </section>
    );
  }

  if (institucion === null) {
    return (
      <section aria-labelledby="titulo-config">
        <h2 id="titulo-config">Configuración de la institución</h2>
        {error !== null ? (
          <div className="aviso-caja error" role="alert">
            {error}
          </div>
        ) : (
          <p role="status">Cargando configuración…</p>
        )}
      </section>
    );
  }

  const zonaCambiada = zona !== institucion.timeZone;

  return (
    <section aria-labelledby="titulo-config">
      <h2 id="titulo-config">Configuración de la institución</h2>
      <p className="ayuda">
        Estos datos se imprimen en los recibos y reportes nuevos. Los comprobantes ya
        emitidos <strong>no cambian</strong>: se regeneran desde los datos guardados con cada
        pago.
      </p>

      {aviso !== null && (
        <div className="aviso-caja ok" role="status">
          {aviso}
        </div>
      )}
      {error !== null && (
        <div className="aviso-caja error" role="alert">
          {error}
        </div>
      )}

      <form className="tarjeta" onSubmit={(evento) => void guardar(evento)}>
        <div className="campos">
          <label>
            Nombre de la institución
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              required
              minLength={3}
              maxLength={160}
            />
          </label>
          <label>
            Razón social <span className="ayuda">(opcional)</span>
            <input
              type="text"
              value={razonSocial}
              onChange={(e) => setRazonSocial(e.target.value)}
              maxLength={160}
            />
          </label>
          <label>
            NIT <span className="ayuda">(opcional)</span>
            <input type="text" value={nit} onChange={(e) => setNit(e.target.value)} maxLength={40} />
          </label>
          <label>
            Teléfono <span className="ayuda">(opcional)</span>
            <input
              type="tel"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              maxLength={40}
            />
          </label>
          <label className="ancho-completo">
            Dirección <span className="ayuda">(opcional)</span>
            <input
              type="text"
              value={direccion}
              onChange={(e) => setDireccion(e.target.value)}
              maxLength={300}
            />
          </label>
          <label>
            Zona horaria
            <select value={zona} onChange={(e) => setZona(e.target.value)}>
              {ZONAS.map((identificador) => (
                <option key={identificador} value={identificador}>
                  {identificador}
                </option>
              ))}
            </select>
          </label>
          <label className="casilla">
            <input
              type="checkbox"
              checked={permiteExcedente}
              onChange={(e) => setPermiteExcedente(e.target.checked)}
            />
            Permitir excedentes como saldo a favor
          </label>
        </div>

        <p className="ayuda">
          {permiteExcedente
            ? 'Si el importe supera lo pendiente, la diferencia se registra como saldo a favor del estudiante. Nunca se oculta.'
            : 'Un pago que supere lo pendiente será rechazado. El cajero tendrá que ajustar el importe.'}
        </p>

        {/* La zona horaria no es un dato cosmético: se advierte antes de guardarlo. */}
        {zonaCambiada && (
          <div className="aviso-caja" role="alert">
            <strong>Va a cambiar la zona horaria</strong> de {institucion.timeZone} a {zona}. De
            ella dependen la fecha operativa, el cálculo de la mora y la numeración diaria de
            descargas. Cámbiela solo si la institución realmente se mudó de zona.
          </div>
        )}

        <div className="acciones">
          <button type="submit" className="boton primario" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar configuración'}
          </button>
        </div>

        <p className="ayuda">
          Moneda: <strong>{institucion.currency}</strong>. Cambiarla afectaría a todos los
          importes históricos, así que no se edita desde aquí.
        </p>
      </form>
    </section>
  );
}
